import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { bonusDefApi, F933 } from '../../../services/bonusDefApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useBonusDefForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F933[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [memo, setMemo] = useState('');
  const [amount, setAmount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bonusDefApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.f933_01);
      setName(current.f933_11);
      setDate(current.f933_12 ? current.f933_12.substring(0, 10) : '');
      setMemo(current.f933_13);
      setAmount(current.f933_21);
      setTaxRate(current.f933_22);
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setName('');
    setDate('');
    setMemo('');
    setAmount(0);
    setTaxRate(0);
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.f933_01);
    setName(current.f933_11);
    setDate(current.f933_12 ? current.f933_12.substring(0, 10) : '');
    setMemo(current.f933_13);
    setAmount(current.f933_21);
    setTaxRate(current.f933_22);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.f933_01);
      setName(current.f933_11);
      setDate(current.f933_12 ? current.f933_12.substring(0, 10) : '');
      setMemo(current.f933_13);
      setAmount(current.f933_21);
      setTaxRate(current.f933_22);
    }
  };

  // Save
  const save = async () => {
    try {
      const payload = {
        f933_01: code,
        f933_11: name,
        f933_12: date,
        f933_13: memo,
        f933_21: amount,
        f933_22: taxRate,
      };
      if (mode === 'add') {
        await bonusDefApi.create(payload);
        message.success('新增成功');
      } else {
        await bonusDefApi.update(current!.f933_01, payload);
        message.success('修改成功');
      }
      const data = await loadRecords();
      const idx = data.findIndex(r => r.f933_01 === code);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await bonusDefApi.delete(current.f933_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f933_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode,
    name, setName,
    date, setDate,
    memo, setMemo,
    amount, setAmount,
    taxRate, setTaxRate,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
